import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { usePromoAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  LogIn, Eye, EyeOff, User2, Lock, ShieldCheck, KeyRound, Sparkles,
  Trophy, Gift, Zap, Wallet, Crown, Diamond, BadgePercent, Star,
} from "lucide-react";

const fmtBdtCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1_00_00_000) return `৳ ${(v / 1_00_00_000).toFixed(1)} Cr+`;
  if (v >= 1_00_000)    return `৳ ${(v / 1_00_000).toFixed(1)} L+`;
  if (v >= 1_000)       return `৳ ${(v / 1_000).toFixed(1)} K+`;
  return `৳ ${v.toFixed(0)}`;
};

export default function PromoLogin() {
  const { account, login, verify2FA, initializing } = usePromoAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perkIdx, setPerkIdx] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [totp, setTotp] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  // Live admin-driven highlights (commission_pct, payout cadence, lifetime paid)
  const [highlights, setHighlights] = useState({
    commission_pct: 25,
    period_days: 15,
    min_payout: 0,
    lifetime_paid_to_affiliates: 0,
    active_affiliates: 0,
  });

  useEffect(() => {
    let alive = true;
    api.get("/aff-panel/public/highlights")
      .then((r) => { if (alive && r?.data) setHighlights((h) => ({ ...h, ...r.data })); })
      .catch(() => { /* silent — fall back to defaults */ });
    return () => { alive = false; };
  }, []);

  // Dynamic perks list — commission % comes from admin settings
  const PERKS = [
    { icon: Wallet, title: "Same-day payouts", desc: "Binance Pay · USDT BEP/TRC · bKash · Nagad — settled in under 24h" },
    { icon: Gift, title: "Custom promo codes", desc: "Brand-it-yourself short links, click-attributed in real time" },
    { icon: Trophy, title: `Up to ${highlights.commission_pct}% commission`, desc: `Of net loss every ${highlights.period_days} days — among the highest in Bangladesh` },
    { icon: Zap, title: "Live operations dashboard", desc: "Player activity, deposits and earnings — refreshed by the minute" },
    { icon: Crown, title: "5-Tier VIP program", desc: "Bronze → Diamond — stacking bonuses, dedicated account manager from Gold" },
  ];

  // Dynamic brag stats (lifetime + period + SLA + support)
  const BRAGS = [
    {
      kpi: highlights.lifetime_paid_to_affiliates > 0
        ? fmtBdtCompact(highlights.lifetime_paid_to_affiliates)
        : "৳ 12.4 M+",
      label: "Paid to affiliates",
    },
    { kpi: `${highlights.commission_pct}%`, label: "Max commission" },
    { kpi: `${highlights.period_days} days`, label: "Payout cycle" },
    { kpi: "24 / 7", label: "Account manager" },
  ];

  useEffect(() => {
    const t = setInterval(() => setPerkIdx((i) => (i + 1) % PERKS.length), 3800);
    return () => clearInterval(t);
  }, [PERKS.length]);

  if (!initializing && account) return <Navigate to="/promo/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return toast.error("Username & password required");
    setBusy(true);
    try {
      const res = await login(username.trim(), password);
      if (res?.requires_2fa) {
        setChallenge({ challenge_token: res.challenge_token });
        toast.info("Enter the 6-digit code from your authenticator");
      } else {
        toast.success("Welcome to the Premium Affiliate Suite");
        navigate("/promo/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    const payload = useBackup
      ? { challenge_token: challenge.challenge_token, backup_code: backupCode.trim() }
      : { challenge_token: challenge.challenge_token, totp_code: totp.trim() };
    if (!useBackup && !/^\d{6}$/.test(totp.trim())) return toast.error("Enter the 6-digit code");
    if (useBackup && !backupCode.trim()) return toast.error("Enter a backup code");
    setBusy(true);
    try {
      await verify2FA(payload);
      toast.success("Welcome to the Premium Affiliate Suite");
      navigate("/promo/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "2FA verification failed");
    } finally {
      setBusy(false);
    }
  };

  const Perk = PERKS[perkIdx];

  return (
    <div className="min-h-screen w-full text-white relative promo-bg-auth overflow-hidden">
      {/* Decorative orbs */}
      <div aria-hidden className="absolute top-[-200px] left-[-200px] w-[520px] h-[520px] rounded-full" style={{
        background: "radial-gradient(closest-side, rgba(34, 192, 115, 0.12), transparent 70%)",
        filter: "blur(10px)",
      }} />
      <div aria-hidden className="absolute bottom-[-180px] right-[-160px] w-[460px] h-[460px] rounded-full" style={{
        background: "radial-gradient(closest-side, rgba(16, 185, 129, 0.08), transparent 70%)",
        filter: "blur(8px)",
      }} />
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />

      <div className="relative max-w-6xl mx-auto px-4 py-8 lg:py-14 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center min-h-screen">
        {/* Left: hero */}
        <div className="order-2 lg:order-1 p-fade-up">
          <div className="p-gold-chip mb-5">
            <Sparkles size={11} /> Maxlive88 Affiliate Programme
          </div>
          <h1 className="text-[42px] sm:text-[58px] font-black leading-[1.02] tracking-tight mb-4">
            <span className="p-platinum-text">Built to reward</span><br />
            <span className="p-gold-text">serious partners.</span>
          </h1>
          <p className="text-white/65 text-base sm:text-[17px] mb-8 max-w-md leading-relaxed">
            The operations console for Maxlive88 affiliates &mdash; live attribution,
            net-loss commission of up to <span className="text-emerald-300 font-bold">{highlights.commission_pct}%</span>,
            and payouts every <span className="text-emerald-300 font-bold">{highlights.period_days} days</span>.
            One platform, one dedicated account manager, zero guesswork.
          </p>

          {/* Brag stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7 max-w-2xl">
            {BRAGS.map((b, i) => (
              <div key={i} className="p-glass-light px-4 py-3 p-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="text-[19px] font-black p-gold-text leading-none">{b.kpi}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-1.5">{b.label}</div>
              </div>
            ))}
          </div>

          {/* Perks list (desktop) */}
          <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {PERKS.map((p, i) => {
              const Icon = p.icon;
              const active = i === perkIdx;
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3.5 rounded-xl transition-all duration-300"
                  style={{
                    background: active
                      ? "linear-gradient(135deg, rgba(34, 192, 115, 0.16), rgba(34, 192, 115, 0.02))"
                      : "rgba(255,255,255,0.025)",
                    border: `1px solid ${active ? "rgba(34, 192, 115, 0.55)" : "rgba(255,255,255,0.06)"}`,
                    boxShadow: active ? "0 12px 30px rgba(34, 192, 115, 0.15)" : "none",
                  }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{
                    background: "linear-gradient(135deg, rgba(34, 192, 115, 0.25), rgba(127, 29, 29, 0.08))",
                    border: "1px solid rgba(34, 192, 115, 0.45)",
                    color: "#a7f3d0",
                  }}>
                    <Icon size={17} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-bold text-white">{p.title}</div>
                    <div className="text-[11.5px] text-white/55 leading-snug">{p.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Perk mobile */}
          <div className="lg:hidden flex items-center gap-3 px-4 py-3 rounded-xl bg-white/4 border border-white/10 mt-3">
            <Perk.icon size={18} className="text-emerald-300" />
            <div>
              <div className="text-sm font-bold">{Perk.title}</div>
              <div className="text-[11px] text-white/55">{Perk.desc}</div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="order-1 lg:order-2 p-fade-up">
          <div className="w-full max-w-md mx-auto p-glass" style={{
            padding: "40px 36px 32px",
            boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
          }}>
            {!challenge ? (
              <>
                <div className="mb-7">
                  <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/[0.06]">
                    <div className="p-brand-mark" style={{ width: 38, height: 38, fontSize: 12 }}>M88</div>
                    <div className="leading-tight">
                      <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">Affiliate Portal</div>
                      <div className="text-[11.5px] text-white/65 mt-0.5 inline-flex items-center gap-1.5">
                        <ShieldCheck size={11} className="text-emerald-400/80" /> Secure session · TLS 1.3
                      </div>
                    </div>
                  </div>
                  <h2 className="text-[22px] font-bold tracking-tight text-white" style={{ fontFamily: "'IBM Plex Sans', 'Plus Jakarta Sans', sans-serif" }}>
                    Sign in to your account
                  </h2>
                  <p className="text-white/50 text-[12.5px] mt-1.5 leading-relaxed">
                    Use the credentials issued by your Maxlive88 account manager.
                  </p>
                </div>
                <form onSubmit={submit} className="space-y-3.5" data-testid="promo-login-form">
                  <Field label="Username">
                    <User2 size={14} className="text-emerald-300/70" />
                    <input
                      value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username"
                      placeholder="aff_yourname" data-testid="promo-login-username"
                    />
                  </Field>
                  <Field label="Password">
                    <Lock size={14} className="text-emerald-300/70" />
                    <input
                      type={showPwd ? "text" : "password"}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder="••••••••" data-testid="promo-login-password"
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="text-white/40 hover:text-emerald-200" aria-label="toggle visibility">
                      {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </Field>
                  <button type="submit" disabled={busy}
                    className="w-full h-12 rounded-xl p-btn-gold inline-flex items-center justify-center gap-2 text-[13px] mt-2"
                    data-testid="promo-login-submit">
                    <LogIn size={15} /> {busy ? "Signing in…" : "Sign in"}
                  </button>
                </form>
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-white/[0.06]" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">or</span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>
                <a href="/profile/affiliate-apply" className="block text-center text-[12.5px] text-white/55 hover:text-white transition">
                  Not a partner yet? <span className="font-semibold text-emerald-300 hover:text-emerald-200">Apply for an account →</span>
                </a>
              </>
            ) : (
              <>
                <div className="mb-7">
                  <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/[0.06]">
                    <div className="p-brand-mark p-pulse-gold" style={{ width: 38, height: 38, fontSize: 11 }}>2FA</div>
                    <div className="leading-tight">
                      <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">Two-factor verification</div>
                      <div className="text-[11.5px] text-white/65 mt-0.5 inline-flex items-center gap-1.5">
                        <KeyRound size={11} className="text-emerald-400/80" /> Identity confirmation required
                      </div>
                    </div>
                  </div>
                  <h2 className="text-[22px] font-bold tracking-tight text-white" style={{ fontFamily: "'IBM Plex Sans', 'Plus Jakarta Sans', sans-serif" }}>
                    Confirm it&apos;s you
                  </h2>
                  <p className="text-white/50 text-[12.5px] mt-1.5 leading-relaxed">{useBackup ? "Enter one of your single-use backup codes." : "Enter the 6-digit code from your authenticator app."}</p>
                </div>
                <form onSubmit={submit2FA} className="space-y-3" data-testid="promo-2fa-form">
                  {!useBackup ? (
                    <Field label="TOTP Code">
                      <ShieldCheck size={14} className="text-emerald-300/70" />
                      <input value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric" maxLength={6} placeholder="000000"
                        style={{ letterSpacing: "0.42em", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 18 }}
                        data-testid="promo-2fa-code" />
                    </Field>
                  ) : (
                    <Field label="Backup Code">
                      <KeyRound size={14} className="text-emerald-300/70" />
                      <input value={backupCode} onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                        style={{ letterSpacing: "0.25em", fontFamily: "ui-monospace, Menlo, monospace" }}
                        placeholder="XXXXX-XXXXX" data-testid="promo-backup-code" />
                    </Field>
                  )}
                  <button type="submit" disabled={busy} className="w-full h-12 rounded-xl p-btn-gold" data-testid="promo-2fa-submit">
                    {busy ? "Verifying…" : "Verify & Enter"}
                  </button>
                  <div className="flex items-center justify-between text-[11.5px] text-white/55 pt-2">
                    <button type="button" onClick={() => { setChallenge(null); setTotp(""); setBackupCode(""); }} className="underline hover:text-emerald-200">← Back</button>
                    <button type="button" onClick={() => setUseBackup(!useBackup)} className="underline hover:text-emerald-200">
                      {useBackup ? "Use TOTP code" : "Use backup code"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Trust strip */}
          <div className="mt-5 flex items-center justify-center gap-5 text-[10.5px] uppercase tracking-[0.22em] text-white/35">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={12} /> Bank-grade encryption</span>
            <span className="inline-flex items-center gap-1"><BadgePercent size={12} /> Anjouan licensed</span>
            <span className="inline-flex items-center gap-1"><Star size={12} /> Verified payouts</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="p-label">{label}</label>
      <div className="p-input-shell">
        {children}
      </div>
    </div>
  );
}
